import { useTranslation } from "react-i18next";
import { ButtonTypes } from "../../Definitions";
import { colors, fonts, glyphs, useMediaQuery } from "../../Styles";
import { NewButton, TableButton } from "../BaseComponents";
import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { TablePrefs } from "./subComponents/tablePrefsWidget";

/* eslint-disable */
export enum TableTypes {
  fullPage = "fullPage",
  fitContent = "fitContent"
}
/* eslint-enable */

interface TableLayoutProps {
  zIndex?: number
  tableType?: TableTypes;
  searchbBarArea?: JSX.Element;
  filterDisplayListArea?: JSX.Element;
  buttonArea?: JSX.Element;
  tableArea?: JSX.Element;
  additionalArea?: JSX.Element;
  paginationBtns?: {
    onNext: () => void;
    onPrev: () => void;
    canPreviousPage: boolean;
    canNextPage: boolean;
    thisPageIndex: number;
    pageCount: number;
  };
  editableTable?: boolean;
  hideOnMobile?: boolean;
}

export default function TableLayout({ tableType = TableTypes.fullPage, searchbBarArea, filterDisplayListArea, buttonArea, tableArea, additionalArea, paginationBtns, editableTable = false, zIndex, hideOnMobile = false }: TableLayoutProps) {
  const { t } = useTranslation();
  const [tablePrefsState, setTablePrefsState] = useState<boolean>(false); 
  const tablePrefsRef = useRef<HTMLDivElement | null>(null);
  const tableHeightRef = useRef<HTMLDivElement | null>(null);
  const isEditableTable: boolean = editableTable && editableTable === true
  const mobileXs = useMediaQuery(`(max-width: 650px)`);
  const mobile = useMediaQuery(`(max-width: 1080px)`);
  const popoutTablePrefs = useMediaQuery(`(max-width: 1000px)`);
  const paginationButtonsPosition = useMediaQuery(`(max-width: 1000px)`);

  const _gridTemplateRows = paginationBtns ? tableType === TableTypes.fitContent ? "50px fit-content(100%) 40px" : "50px 1fr 40px" : "50px 1fr"

  const tablePrefs = ( tableHeightRef.current && additionalArea && 
    <TablePrefs content={additionalArea} popout={popoutTablePrefs} tableHeight={mobile ? "100%" : tableType === TableTypes.fullPage ? "100%" : `${tableHeightRef.current.offsetHeight}px`}/>
  )

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        tablePrefsState &&
        tablePrefsRef.current &&
        !tablePrefsRef.current.contains(event.target as Node)
      ) {
        setTablePrefsState(false);
      }
    }
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [tablePrefsState]);

  const paginationText =  paginationBtns && <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary }}>
    {`${t("page", { defaultValue: "page" })} ${ paginationBtns.thisPageIndex} ${t("of", { defaultValue: "of" })} ${ paginationBtns.pageCount}`}
  </p>
  
  return (
    <div style={{ height: "100%", width: "100%", display: "grid", gridTemplateRows: _gridTemplateRows, boxSizing: "border-box", position: "relative" }}>

      {/* Top Area */}
      <div style={{ gridArea: "1/1/2/2", boxSizing: "border-box", width: "100%", padding: "0px 20px", display: "grid", gridTemplateColumns: mobile ? "1fr" : isEditableTable ? "280px 1fr 390px" : "280px 1fr 272px", gridTemplateRows: "50px" }}>
        {mobileXs && hideOnMobile ? undefined : searchbBarArea && <div style={{ marginBottom: "5px", gridArea: "1/1/2/2", display: "flex", alignItems: "end" }}>
          {searchbBarArea}
        </div>}

        {!mobile && filterDisplayListArea && <div style={{ gridArea: "1/2/2/3", display: "flex", alignItems: "end", position: "relative" }}>
          {filterDisplayListArea}
        </div>}

        {buttonArea && <div style={{ display: "flex", justifyContent: "end", gap: "5px", gridArea: "1/3/2/4", zIndex: zIndex ? zIndex : 1 /* at least 1 to stay on top of table */}}>
          {buttonArea}
        </div>}
      </div>

      {/* Table Area */}
      {tableArea && <div ref={tableHeightRef} style={{ gridArea: "2/1/3/2", height: "100%", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid " + colors.greyPrimary, borderRadius: "10px", overflow: "hidden", zIndex: zIndex ? zIndex : 0 }}>
        {tableArea}
      </div>}

      {/* Blue Print */}
      {popoutTablePrefs && additionalArea && editableTable && (
        <div ref={tablePrefsRef} style={{zIndex: zIndex ? zIndex : 1, position: "absolute", transition: "all ease-in-out 0.5s", left: tablePrefsState ? mobileXs ? "0px" : "10px" : "-310px", bottom: tableType === TableTypes.fullPage ? "100px" : undefined}}>
          <TableButton
            content={t("animalPages.animals.tablePreferences", { defaultValue: "Table preferences" })}
            disabled={false}
            onClick={() => {setTablePrefsState(!tablePrefsState)}}
            startEnhancer={tablePrefsState ? glyphs.Close({}) : glyphs.Arrow({})}
            bottomPosition={220}
            leftPosition={305}
          />
          {tablePrefs}
        </div>
      )}

      { /* Additional Area */}
      {!popoutTablePrefs && additionalArea && editableTable && createPortal(
         <div style={{ zIndex: zIndex ? zIndex : 1, position: "absolute", top: "210px", bottom: "125px", left: "20px" }}>{tablePrefs}</div>,
      document.body)}
      
      {/* Pagination Buttons Area */}
      {paginationBtns && <div style={{ gridArea: "3/1/4/2", display: "flex", alignItems: "center", justifyContent: paginationButtonsPosition ? "center" : "flex-start", gap: paginationButtonsPosition ? "10px" : "5px", padding: "5px 20px 0px 20px", zIndex: zIndex ? zIndex : 0 }}>
        <NewButton
          content={""}
          buttonType={ButtonTypes.arrowButton}
          onClick={paginationBtns.onPrev}
          startEnhancer={glyphs.Arrow({ rotate: 180 })}
          overrides={{
            baseStyles: { backgroundColor: colors.greyPrimary },
            hoverStyles: { backgroundColor: colors.greySecondary },
          }}
          disabled={isEditableTable || paginationBtns.canPreviousPage}
        />
        {paginationButtonsPosition && paginationText }
        <NewButton
          content={""}
          buttonType={ButtonTypes.arrowButton}
          onClick={paginationBtns.onNext}
          startEnhancer={glyphs.Arrow({})}
          overrides={{
            baseStyles: { backgroundColor: colors.greyPrimary },
            hoverStyles: { backgroundColor: colors.greySecondary },
          }}
          disabled={isEditableTable || paginationBtns.canNextPage}
        />
        {!paginationButtonsPosition && paginationText }
      </div>}
    </div>
  )
}